/**
 * Query key registries for the analytics widgets. Every key ends with the
 * request scope from `scopeKey()`: the audit year the data was fetched for
 * (`null` = all years) or `demo:<nonce>` for demo data, so each has its own
 * cache entry. Hooks pass `scopeKey(useAnalyticsScope())`; widget previews
 * use the default `null`.
 */

/** Controller widgets (reports #20-#28). */

const controllerRoot = ['analytics', 'controller'] as const;

export const analyticsControllerQueryKeys = {
  all: controllerRoot,
  testSummary: (scope?: string | null) => [...controllerRoot, 'test-summary', scope ?? null] as const,
  failingControls: (scope?: string | null) => [...controllerRoot, 'failing-controls', scope ?? null] as const,
  exceptionRate: (scope?: string | null) => [...controllerRoot, 'exception-rate', scope ?? null] as const,
  sampleSize: (scope?: string | null) => [...controllerRoot, 'sample-size', scope ?? null] as const,
  rollforward: (scope?: string | null) => [...controllerRoot, 'rollforward', scope ?? null] as const,
  ipeStatus: (scope?: string | null) => [...controllerRoot, 'ipe-status', scope ?? null] as const,
  todToe: (scope?: string | null) => [...controllerRoot, 'tod-toe', scope ?? null] as const,
  sodVerified: (scope?: string | null) => [...controllerRoot, 'sod-verified', scope ?? null] as const,
  evidenceCoverage: (scope?: string | null) => [...controllerRoot, 'evidence-coverage', scope ?? null] as const,
} as const;

/** Query key registry for Feature 5.3 analytics dashboards (CAE/Manager persona, reports #9-#19). */
const caeRoot = ['analytics', 'cae'] as const;

export const analyticsCaeQueryKeys = {
  all: caeRoot,
  controlUniverse: (scope?: string | null) => [...caeRoot, 'control-universe', scope ?? null] as const,
  testingStatus: (scope?: string | null) => [...caeRoot, 'testing-status', scope ?? null] as const,
  issuesPipeline: (scope?: string | null) => [...caeRoot, 'issues-pipeline', scope ?? null] as const,
  coverageMatrix: (scope?: string | null) => [...caeRoot, 'coverage-matrix', scope ?? null] as const,
  mrc: (scope?: string | null) => [...caeRoot, 'mrc', scope ?? null] as const,
  exceptionsSummary: (scope?: string | null) => [...caeRoot, 'exceptions-summary', scope ?? null] as const,
  auditorReliance: (scope?: string | null) => [...caeRoot, 'auditor-reliance', scope ?? null] as const,
  actionPlanAging: (scope?: string | null) => [...caeRoot, 'action-plan-aging', scope ?? null] as const,
  controlsByDept: (scope?: string | null) => [...caeRoot, 'controls-by-dept', scope ?? null] as const,
  itgcCoverage: (scope?: string | null) => [...caeRoot, 'itgc-coverage', scope ?? null] as const,
  testerWorkload: (scope?: string | null) => [...caeRoot, 'tester-workload', scope ?? null] as const,
} as const;

/** Query key registry for Feature 5.3 analytics dashboards (CFO/Executive persona, reports #1-#8). */
const cfoRoot = ['analytics', 'cfo'] as const;

export const analyticsCfoQueryKeys = {
  all: cfoRoot,
  complianceScore: (scope?: string | null) => [...cfoRoot, 'compliance-score', scope ?? null] as const,
  coverageHeatmap: (scope?: string | null) => [...cfoRoot, 'coverage-heatmap', scope ?? null] as const,
  openIssues: (scope?: string | null) => [...cfoRoot, 'open-issues', scope ?? null] as const,
  programProgress: (scope?: string | null) => [...cfoRoot, 'program-progress', scope ?? null] as const,
  socCoverage: (scope?: string | null) => [...cfoRoot, 'soc-coverage', scope ?? null] as const,
  riskLandscape: (scope?: string | null) => [...cfoRoot, 'risk-landscape', scope ?? null] as const,
  repeatEscalated: (scope?: string | null) => [...cfoRoot, 'repeat-escalated', scope ?? null] as const,
  scoreTrend: (scope?: string | null) => [...cfoRoot, 'score-trend', scope ?? null] as const,
} as const;

/** Query key registry for Feature 5.3 analytics dashboards (Tester "my work" persona, reports #30-#36 + feedback round 2026-08). */
const testerRoot = ['analytics', 'tester'] as const;

export const analyticsTesterQueryKeys = {
  all: testerRoot,
  myControls: (assigneeUserId?: number | null, scope?: string | null) => [...testerRoot, 'my-controls', assigneeUserId ?? null, scope ?? null] as const,
  dueThisWeek: (assigneeUserId?: number | null, scope?: string | null) => [...testerRoot, 'due-this-week', assigneeUserId ?? null, scope ?? null] as const,
  myProgress: (assigneeUserId?: number | null, scope?: string | null) => [...testerRoot, 'my-progress', assigneeUserId ?? null, scope ?? null] as const,
  myExceptions: (assigneeUserId?: number | null, scope?: string | null) => [...testerRoot, 'my-exceptions', assigneeUserId ?? null, scope ?? null] as const,
  evidenceChecklist: (runId: string | undefined, scope?: string | null) => [...testerRoot, 'evidence-checklist', runId ?? null, scope ?? null] as const,
  rerunsRequired: (assigneeUserId?: number | null, scope?: string | null) => [...testerRoot, 'reruns-required', assigneeUserId ?? null, scope ?? null] as const,
  avgTestTime: (scope?: string | null) => [...testerRoot, 'avg-test-time', scope ?? null] as const,
  testers: (scope?: string | null) => [...testerRoot, 'testers', scope ?? null] as const,
} as const;

/** Query key registry for Feature 5.3 analytics dashboards (External Auditor persona, reports #37-#41 + feedback round 2026-08). */
const auditorRoot = ['analytics', 'auditor'] as const;

export const analyticsAuditorQueryKeys = {
  all: auditorRoot,
  controlInventory: (scope?: string | null) => [...auditorRoot, 'control-inventory', scope ?? null] as const,
  workpapersPackage: (controlId: string | undefined, scope?: string | null) => [...auditorRoot, 'workpapers-package', controlId ?? null, scope ?? null] as const,
  exceptionReport: (scope?: string | null) => [...auditorRoot, 'exception-report', scope ?? null] as const,
  issueLedger: (scope?: string | null) => [...auditorRoot, 'issue-ledger', scope ?? null] as const,
  relianceEligible: (scope?: string | null) => [...auditorRoot, 'reliance-eligible', scope ?? null] as const,
  processChartingStatus: (scope?: string | null) => [...auditorRoot, 'process-charting-status', scope ?? null] as const,
} as const;

/**
 * Query key registry for the new `/api/analytics/scope/*` endpoints
 * (feedback round 2026-08) — shared cross-persona scope/coverage data (CFO,
 * CAE, Controller, and Auditor dashboards all pull from these same keys).
 */
const scopeRoot = ['analytics', 'scope'] as const;

export const analyticsScopeQueryKeys = {
  all: scopeRoot,
  controlCounts: (scope?: string | null) => [...scopeRoot, 'control-counts', scope ?? null] as const,
  riskSummary: (scope?: string | null) => [...scopeRoot, 'risk-summary', scope ?? null] as const,
  systems: (scope?: string | null) => [...scopeRoot, 'systems-in-scope', scope ?? null] as const,
  entities: (scope?: string | null) => [...scopeRoot, 'entities-in-scope', scope ?? null] as const,
  materiality: (scope?: string | null) => [...scopeRoot, 'materiality', scope ?? null] as const,
  plannedCompleted: (scope?: string | null) => [...scopeRoot, 'planned-vs-completed', scope ?? null] as const,
  overdueSummary: (scope?: string | null) => [...scopeRoot, 'overdue-summary', scope ?? null] as const,
  testingStatusRollup: (scope?: string | null) => [...scopeRoot, 'testing-status-rollup', scope ?? null] as const,
} as const;

/** Query key registry for the new Reviewer persona (dashboard_key 'reviewer', feedback round 2026-08). */
const reviewerRoot = ['analytics', 'reviewer'] as const;

export const analyticsReviewerQueryKeys = {
  all: reviewerRoot,
  queue: (scope?: string | null) => [...reviewerRoot, 'queue', scope ?? null] as const,
  summary: (scope?: string | null) => [...reviewerRoot, 'summary', scope ?? null] as const,
} as const;
